import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('token'));

  const checkAuth = useCallback(async () => {
    console.log('[Auth] Checking authentication status...');
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      console.log('[Auth] User authenticated:', response.data.email);
      setUser(response.data);
    } catch (error) {
      console.log('[Auth] Not authenticated or session expired');
      setUser(null);
      setToken(null);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    console.log('[Auth] Login attempt:', email);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      const { token: newToken, user: userData } = response.data;
      console.log('[Auth] Login successful:', userData.email, '- Role:', userData.role);
      setToken(newToken);
      localStorage.setItem('token', newToken);
      setUser(userData);
      return userData;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Login failed';
      console.error('[Auth] Login failed:', errorMessage, '- Email:', email);
      // Re-throw with the specific error message
      const enhancedError = new Error(errorMessage);
      enhancedError.response = error.response;
      throw enhancedError;
    }
  };

  const register = async (userData) => {
    console.log('[Auth] Registration attempt:', userData.email, '- Role:', userData.role);
    try {
      const response = await axios.post(`${API}/auth/register`, userData, { withCredentials: true });
      const { token: newToken, user: newUser } = response.data;
      console.log('[Auth] Registration successful:', newUser.email, '- User ID:', newUser.user_id);
      setToken(newToken);
      localStorage.setItem('token', newToken);
      setUser(newUser);
      return newUser;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Registration failed';
      console.error('[Auth] Registration failed:', errorMessage, '- Email:', userData.email);
      // Re-throw with the specific error message
      const enhancedError = new Error(errorMessage);
      enhancedError.response = error.response;
      throw enhancedError;
    }
  };

  const googleAuth = async (sessionId, role = 'renter') => {
    console.log('[Auth] Google auth attempt - Role:', role);
    try {
      const response = await axios.post(`${API}/auth/google/session`, { session_id: sessionId, role }, { withCredentials: true });
      const { token: newToken, user: userData } = response.data;
      console.log('[Auth] Google auth successful:', userData.email);
      setToken(newToken);
      localStorage.setItem('token', newToken);
      setUser(userData);
      return userData;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Google authentication failed';
      console.error('[Auth] Google auth failed:', errorMessage);
      const enhancedError = new Error(errorMessage);
      enhancedError.response = error.response;
      throw enhancedError;
    }
  };

  const logout = async () => {
    console.log('[Auth] Logout initiated for:', user?.email);
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      console.log('[Auth] Logout successful');
    } catch (error) {
      console.error('[Auth] Logout error:', error.response?.data?.detail || error.message);
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
  };

  const updateProfile = async (data) => {
    console.log('[Auth] Profile update attempt');
    try {
      const response = await axios.put(`${API}/auth/profile`, data, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      console.log('[Auth] Profile updated successfully');
      setUser(response.data);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Profile update failed';
      console.error('[Auth] Profile update failed:', errorMessage);
      const enhancedError = new Error(errorMessage);
      enhancedError.response = error.response;
      throw enhancedError;
    }
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    googleAuth,
    logout,
    updateProfile,
    checkAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
